/**
 * Review Traveller Thunks
 *
 * Async thunks for review traveller form operations.
 * This is the Review Traveller API, not a generic traveller API.
 *
 * Design: Thunks return API response as-is (FlightsReviewTravellersTemplateData).
 * NO transformations in thunks - all transformations happen in selectors.
 * This keeps raw API data in Redux for debugging and ensures single source of truth.
 *
 * On successful fetch, the thunk dispatches setActiveTemplate to update the active template.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { TEMPLATE_TYPES } from '@/features/chat/constants/templateTypes';
import {
  fetchReviewTravellers as fetchReviewTravellersApi,
  validateTravellerForm as validateTravellerFormApi,
  ReviewTravellerError,
  ReviewTravellerValidationError,
  type ReviewTravellerErrorDetails,
} from '../api/reviewTravellerApi';
import type { TravellerFormRequestParams, ValidateTravellerResponse } from '../types';
import { logger } from '@/shared/utils/logger';
import type {
  FlightsReviewTravellersTemplateData,
  TravellerFormData,
  ValidateTravellerFormRequestParams,
} from '@contracts';
import { getOrCreateSessionId, getUUID } from '@/shared/utils';
import {
  setActiveTemplate,
  clearPendingTemplate,
} from '@/features/chat/redux/slices/templateSlice';
import { TRACKING_DATA, fireTrackingEvents } from '@shared/analytics';

// ============================================
// Types
// ============================================

/**
 * Thunk returns raw API response data directly.
 * This is FlightsReviewTravellersTemplateData from @contracts.
 * Selectors transform this data for UI consumption.
 */
export type ReviewTravellerThunkResult = FlightsReviewTravellersTemplateData;

/**
 * Reject value type for review traveller thunks.
 * Can be a simple string or structured error details for popup display.
 */
export type ReviewTravellerRejectValue = string | ReviewTravellerErrorDetails;

// Re-export TravellerFormData for backward compatibility
export type { TravellerFormData };

// ============================================
// Thunks
// ============================================

/**
 * Fetch review traveller form details
 *
 * Returns FlightsReviewTravellersTemplateData directly from API.
 * NO transformations - raw data is stored in Redux state.
 * Selectors handle all data transformations for UI consumption.
 *
 * On success, dispatches setActiveTemplate to set 'flights-review-travellers' as active template.
 * The contextHashKey is received from the backend response.
 */
export const fetchReviewTravellerThunk = createAsyncThunk<
  FlightsReviewTravellersTemplateData,
  TravellerFormRequestParams,
  { rejectValue: ReviewTravellerRejectValue }
>('flights/fetchReviewTraveller', async (params, { rejectWithValue, dispatch }) => {
  try {
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const response = await fetchReviewTravellersApi({
      ...params,
      sessionId,
      userId,
    });

    // Validate response has required structure
    if (!response?.data?.travellerDetails?.paxForm?.paxGroups) {
      // Clear pending template on validation failure so interstitial is shown again
      dispatch(clearPendingTemplate());
      return rejectWithValue('Invalid response structure from review traveller API');
    }

    const templateData = response.data;

    // TODO: Replace with backend-driven trackingInfo from API response
    const trackingInfo = TRACKING_DATA[TEMPLATE_TYPES.FLIGHTS_REVIEW_TRAVELLERS] ?? null;
    dispatch(
      setActiveTemplate({
        template: 'flights-review-travellers',
        data: {
          ...templateData,
          canGoBack: true,
          crId: params.crId,
        },
        trackingInfo,
      }),
    );
    fireTrackingEvents(trackingInfo);

    // Return raw template data as-is - NO TRANSFORMATIONS
    // Selectors will transform this data for UI consumption
    return templateData;
  } catch (error) {
    logger.error('[ReviewTravellerThunk] Failed to fetch review traveller details:', error);
    // Clear pending template on error so interstitial is shown again
    dispatch(clearPendingTemplate());
    // Return structured error details for popup display if available
    if (error instanceof ReviewTravellerError && error.errorDetails) {
      return rejectWithValue(error.errorDetails);
    }
    if (error instanceof ReviewTravellerError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch review traveller details');
  }
});

/**
 * Validate traveller form data
 */
export const validateTravellerFormThunk = createAsyncThunk<
  ValidateTravellerResponse,
  ValidateTravellerFormRequestParams,
  { rejectValue: string }
>('flights/validateTravellerForm', async (params, { rejectWithValue }) => {
  try {
    const response = await validateTravellerFormApi(params);
    return response;
  } catch (error) {
    logger.error('[ReviewTravellerThunk] Failed to validate traveller form:', error);
    if (error instanceof ReviewTravellerValidationError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to validate traveller form');
  }
});

// ============================================
// Backward Compatibility Aliases (Deprecated)
// ============================================

/**
 * @deprecated Use fetchReviewTravellerThunk instead
 */
export const fetchTravellerFormDetailsThunk = fetchReviewTravellerThunk;

/**
 * @deprecated Use ReviewTravellerThunkResult instead
 */
export type TravellerFormThunkResult = ReviewTravellerThunkResult;
